import { Headline, HeadlineStyledSpan, Hero, HtmlContainer, Paragraph, TwoColContainer, TwoColRow, TwoColSpan } from '../../ui';
import { CurrencyCode, formatWithCurrency } from '../../lib/money';
import css from './index.module.scss';
import { PropsWithChildren, ReactNode } from 'react';
import { parseIsoDate } from '@n2-common/lib/dateTime';

export type Detail = {
    label: string;
    data: ReactNode;
    footnote?: { mark: string; text: string };
};

export type OfferBoxProps = {
    heading: string;
    amount: number;
    currency: CurrencyCode;
    subHeading?: string;
    description?: string | JSX.Element;
    details?: (Detail | undefined)[];
    note?: string;
};

export default OfferBox;

export function OfferBox({ heading, amount, currency, subHeading, description, children, details = [], note }: OfferBoxProps & PropsWithChildren) {
    const rows = details.filter((d) => !!d) as Detail[];
    return (
        <>
            <Hero className={css.hero}>
                <TwoColContainer>
                    <TwoColSpan>
                        <Headline level={1} asDivElement={true} className={css.heading}>
                            <HeadlineStyledSpan level={6}>{heading}</HeadlineStyledSpan>
                            <HeadlineStyledSpan level={1} className={css.headline}>
                                {formatWithCurrency(amount, currency)}
                            </HeadlineStyledSpan>
                        </Headline>
                    </TwoColSpan>
                    {children}
                    {subHeading && (
                        <TwoColSpan>
                            <Headline level={2}>{subHeading}</Headline>
                        </TwoColSpan>
                    )}
                    {description && (
                        <TwoColSpan>
                            <Paragraph>{description}</Paragraph>
                        </TwoColSpan>
                    )}
                    {rows.map(({ label, data, footnote }, i) => (
                        <TwoColRow key={i} left={`${label}${footnote ? ` ${getMark(i)}` : ''}`} right={data} rightClass={css.amountCol} />
                    ))}
                    {rows.some(({ footnote }) => !!footnote) && (
                        <TwoColSpan className={css.footnote}>
                            {rows.map(({ footnote }, i) => (footnote ? <div key={i}>{`${getMark(i)} ${footnote.text}`}</div> : null))}
                        </TwoColSpan>
                    )}
                </TwoColContainer>
            </Hero>
            {note ? (
                <Hero ui="transparent">
                    <HtmlContainer innerHtml={note} />
                </Hero>
            ) : undefined}
        </>
    );
}

function getMark(i: number, char = '*') {
    if (!getMark.counts[i]) {
        getMark.count++;
        getMark.counts[i] = getMark.count;
    }
    return char.repeat(getMark.counts[i]);
}
getMark.count = 0;
getMark.counts = [] as number[];

export const OpttOutConditionsForOfferBox = ({ country, establishingDate }: { country: Country; establishingDate: IsoDateString }) => {
    const estDate = parseIsoDate(establishingDate) ?? new Date();
    switch (country) {
        case 'SE': {
            const switchDate = parseIsoDate('2025-01-01T00:00:00+01:00');
            return estDate >= switchDate ? (
                <div>
                    <strong>I försäkringen ingår</strong>
                    <ul>
                        <li>Upp till 15 000 kronor i månaden vid sjukskrivning och arbetslöshet.</li>
                        <li>Engångsbetalning vid kritisk sjukdom upp till 100 000 kronor.</li>
                        <li>Engångsbetalning vid dödsfall upp till 500 000 kronor.</li>
                        <li>Ingen bindningstid - du kan säga upp försäkringen när du vill.</li>
                    </ul>
                </div>
            ) : (
                <div>
                    <strong>I försäkringen ingår</strong>
                    <ul>
                        <li>Upp till 16 000 kronor i månaden vid sjukskrivning och arbetslöshet.</li>
                        <li>Engångsbetalning vid kritisk sjukdom upp till 100 000 kronor.</li>
                        <li>Engångsbetalning vid dödsfall upp till 500 000 kronor.</li>
                        <li>Ingen bindningstid - du kan säga upp försäkringen när du vill.</li>
                    </ul>
                </div>
            );
        }
        default:
            return null;
    }
};
